/**
 * MatterTasksTable — the matter's active tasks, as a table.
 *
 * The tab used to render tasks as `ListRow`s, which fold every fact except the
 * title into one grey run-on meta line ("To do · Aug 21, 2026 · 📎2 · 💬1") and
 * park a bare priority flag at the right edge with nothing to say what it is.
 * That reads as a sentence, not a list: nothing lines up down the page, no fact
 * can be sorted, and the flag looks like an error.
 *
 * Tasks are RECORDS, and the questions asked of them are columnar — what, who,
 * when, how urgent, where is it up to. So: Task · Assignee · Due · Priority ·
 * Status, on the shared `Table`, with the row actions (Complete / Archive /
 * Restore) in its own actions menu rather than as hover glyphs.
 *
 * SORTING IS LOCAL AND TOTAL. The tab fetches the matter's tasks in one page
 * (`limit: 100`), so sorting the rows on screen is sorting the set — no
 * partial-sort lie. Due sorts undated rows LAST in both directions: a task with
 * no deadline is not "the earliest deadline".
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  PiArchiveLight,
  PiCheckCircleLight,
  PiChatCircleTextLight,
  PiClockClockwiseLight,
  PiFlagFill,
  PiPaperclipLight,
} from 'react-icons/pi';
import Table from '../../shared/TailwindComponents/Table';
import Badge from '../../shared/TailwindComponents/Badge';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { parseServerDate, formatDateTime } from '../../../utils/functions';
import {
  priorityFlagClass,
  statusBadgeColor,
  taskId,
  taskPriorityLabel,
  taskStatusLabel,
} from './taskConstants';

/** Priority order, so the Priority column sorts by urgency and not by spelling. */
const PRIORITY_WEIGHT: Record<string, number> = {
  critical: 4, high: 3, medium: 2, low: 1,
};

export interface MatterTasksTableProps {
  tasks: any[];
  isLoading?: boolean;
  selectedId?: string | null;
  onSelectTask: (id: string) => void;
  /** Undefined for a task this user may not act on — the action is hidden. */
  canManage: (task: any) => boolean;
  onComplete: (id: string) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}

type SortKey = 'task' | 'assignee' | 'due' | 'priority' | 'status';

const MatterTasksTable: React.FC<MatterTasksTableProps> = ({
  tasks,
  isLoading = false,
  selectedId,
  onSelectTask,
  canManage,
  onComplete,
  onArchive,
  onRestore,
}) => {
  const { t } = useTranslation();
  const { members } = useOrgMembers();

  const [sortKey, setSortKey] = useState<SortKey>('due');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const TASK = t('Task');
  const ASSIGNEE = t('Assignee');
  const DUE = t('Due');
  const PRIORITY = t('Priority');
  const STATUS = t('Status');

  /*
    `Table` flattens a column label to `lower_snake` before handing it to
    `onSort`, and compares `sortBy` against the same flattening — so the maps
    here are keyed the same way, or the header arrow points at a column that is
    not the one being sorted.
  */
  const flatten = (column: string) => column.toLowerCase().replace(/\s+/g, '_');

  const COLUMN_KEY: Record<string, SortKey> = {
    [flatten(TASK)]: 'task',
    [flatten(ASSIGNEE)]: 'assignee',
    [flatten(DUE)]: 'due',
    [flatten(PRIORITY)]: 'priority',
    [flatten(STATUS)]: 'status',
  };

  const KEY_COLUMN: Record<SortKey, string> = {
    task: TASK, assignee: ASSIGNEE, due: DUE, priority: PRIORITY, status: STATUS,
  };

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((member) => map.set(String(member.id), member.name));
    return map;
  }, [members]);

  /** One reading of a task row, so every cell and the sort agree about it. */
  const factsOf = (task: any) => {
    const status = String(task.status || task.Status || 'todo').toLowerCase();
    const collaborators: any[] = task.collaborators || task.Collaborators || [];
    const assigneeId = String(
      task.assigned_to_user_id ?? task.AssignedToUserID ?? task.UserID ?? ''
    );
    return {
      id: taskId(task),
      title: task.title || task.TaskTitle || task.Title || t('Untitled task'),
      status,
      done: status === 'completed',
      archived: status === 'deleted' || status === 'archived',
      due: task.due_date || task.TaskDeadline || task.DueDate || '',
      priority: String(task.priority || task.Priority || '').toLowerCase(),
      collaborators,
      /*
        Best-effort, in the order the payload can actually answer it: an
        explicit name, then a stored user id resolved against the org roster,
        then the first collaborator's name. Anything unresolvable renders the
        standard muted dash rather than an invented person.
      */
      assignee: String(
        task.assigned_to_name || task.assignee_name || task.AssignedTo
        || (assigneeId && memberNames.get(assigneeId))
        || collaborators[0]?.name
        || [collaborators[0]?.first_name, collaborators[0]?.last_name].filter(Boolean).join(' ')
        || ''
      ),
      attachments: typeof task.attachment_count === 'number'
        ? task.attachment_count : (task.Attachments?.length || 0),
      comments: typeof task.comment_count === 'number'
        ? task.comment_count : (task.Comments?.length || 0),
    };
  };

  const sorted = useMemo(() => {
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...tasks].sort((a, b) => {
      const left = factsOf(a);
      const right = factsOf(b);
      switch (sortKey) {
        case 'due': {
          // Undated LAST in both directions — no deadline is not an early one.
          if (!left.due && !right.due) return 0;
          if (!left.due) return 1;
          if (!right.due) return -1;
          return ((parseServerDate(left.due)?.getTime() ?? 0) - (parseServerDate(right.due)?.getTime() ?? 0)) * direction;
        }
        case 'priority':
          return ((PRIORITY_WEIGHT[left.priority] || 0) - (PRIORITY_WEIGHT[right.priority] || 0)) * direction;
        case 'status':
          return t(taskStatusLabel(left.status)).localeCompare(t(taskStatusLabel(right.status))) * direction;
        case 'assignee':
          return left.assignee.localeCompare(right.assignee) * direction;
        default:
          return left.title.localeCompare(right.title, undefined, { numeric: true }) * direction;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, sortKey, sortOrder, memberNames]);

  const columns = [TASK, ASSIGNEE, DUE, PRIORITY, STATUS];

  const rows = sorted.map((task) => {
    const facts = factsOf(task);
    return {
      id: facts.id,
      [TASK]: facts,
      [ASSIGNEE]: facts,
      [DUE]: facts,
      [PRIORITY]: facts,
      [STATUS]: facts,
      /* Clicking a row opens the shared detail container, so the row has to
         show which one is open — `selectedIds` only drives the (absent)
         checkbox column. `Table` honours a row's own className. */
      className: facts.id && facts.id === selectedId ? 'bg-champagne/10' : undefined,
      _raw: task,
      _facts: facts,
    };
  });

  const muted = <span className="text-gray-400 dark:text-gray-500">—</span>;

  const renderCell = (column: string, value: any) => {
    const facts = value as ReturnType<typeof factsOf>;

    if (column === TASK) {
      return (
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={clsx(
              'truncate font-medium',
              facts.done ? 'text-fg-muted line-through' : 'text-fg-body',
              facts.archived && 'text-fg-muted'
            )}
            title={facts.title}
          >
            {facts.title}
          </span>
          {facts.attachments > 0 && (
            <span className="inline-flex flex-shrink-0 items-center gap-0.5 text-[11px] text-fg-muted">
              <PiPaperclipLight className="h-3 w-3" aria-hidden />
              {facts.attachments}
            </span>
          )}
          {facts.comments > 0 && (
            <span className="inline-flex flex-shrink-0 items-center gap-0.5 text-[11px] text-fg-muted">
              <PiChatCircleTextLight className="h-3 w-3" aria-hidden />
              {facts.comments}
            </span>
          )}
        </div>
      );
    }

    if (column === ASSIGNEE) {
      if (facts.collaborators.length > 0) {
        return (
          <div className="flex items-center gap-2 min-w-0">
            <CollaboratorStack collaborators={facts.collaborators} max={3} size="xs" />
            {facts.assignee && (
              <span className="truncate text-fg-body">{facts.assignee}</span>
            )}
          </div>
        );
      }
      return facts.assignee ? <span className="truncate text-fg-body">{facts.assignee}</span> : muted;
    }

    if (column === DUE) {
      /* The backend serialises UTC without a `Z`; `parseServerDate` re-attaches
         it so a task due later today is not drawn as yesterday's overdue. */
      const dueAt = parseServerDate(facts.due);
      if (!dueAt) return muted;
      const overdue = !facts.done && !facts.archived
        && dayjs(dueAt).isBefore(dayjs(), 'day');
      return (
        <span className={clsx(overdue && 'font-medium text-danger-600 dark:text-danger-400')}>
          {formatDateTime(facts.due)}
          {overdue && <span className="ml-1 text-[10px] uppercase">{t('Overdue')}</span>}
        </span>
      );
    }

    if (column === PRIORITY) {
      // The flag is a CELL now, with its label beside it — at the right edge of
      // a row with nothing to name it, it read as a defect rather than a fact.
      if (!facts.priority) return muted;
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <PiFlagFill className={clsx('h-3.5 w-3.5', priorityFlagClass(facts.priority))} aria-hidden />
          <span className="text-fg-body">{t(taskPriorityLabel(facts.priority))}</span>
        </span>
      );
    }

    if (column === STATUS) {
      return (
        <Badge color={statusBadgeColor(facts.status)} className="!text-[10px] !px-1.5 !py-0 whitespace-nowrap">
          {t(taskStatusLabel(facts.status))}
        </Badge>
      );
    }

    return undefined;
  };

  const actions = [
    {
      label: t('Complete'),
      icon: <PiCheckCircleLight />,
      onClick: (row: any) => onComplete(row._facts.id),
      hidden: (row: any) => !canManage(row._raw) || row._facts.done || row._facts.archived,
    },
    {
      label: t('Archive'),
      icon: <PiArchiveLight />,
      isDestructive: true,
      onClick: (row: any) => onArchive(row._facts.id),
      hidden: (row: any) => !canManage(row._raw) || row._facts.archived,
    },
    {
      label: t('Restore'),
      icon: <PiClockClockwiseLight />,
      onClick: (row: any) => onRestore(row._facts.id),
      hidden: (row: any) => !canManage(row._raw) || !row._facts.archived,
    },
  ];

  return (
    <Table
      tableId="matter-tasks"
      columns={columns}
      data={rows}
      isLoading={isLoading}
      renderCell={renderCell}
      actions={actions}
      onRowClick={(row: any) => onSelectTask(row._facts.id)}
      sortableColumns={columns}
      sortBy={flatten(KEY_COLUMN[sortKey])}
      sortOrder={sortOrder}
      onSort={(flattenedColumn, order) => {
        setSortKey(COLUMN_KEY[flattenedColumn] || 'due');
        setSortOrder(order);
      }}
      min="min-h-[16rem]"
      max="max-h-[calc(100vh-360px)]"
      defaultColumnWidths={{
        [TASK]: 360, [ASSIGNEE]: 180, [DUE]: 160, [PRIORITY]: 130, [STATUS]: 130,
      }}
    />
  );
};

export default MatterTasksTable;
